/**
 * Parcels
 */

// React
import React, { FunctionComponent, useEffect, useState, Fragment } from 'react';
import Helper from '../../utils/tableBuilder';
import { Link } from 'react-router-dom';

import RenderFilter from '../../utils/listDaynamicFilterHookForm';
import { useForm } from 'react-hook-form';

// UI
import theme from '../../theme';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  Grid,
  DialogTitle,
  IconButton,
  TextField
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiArrowRight,
  mdiCheck,
  mdiClose,
  mdiMapMarkerOutline,
  mdiKeyOutline,
  mdiStateMachine,
  mdiFileDocumentOutline,
  mdiHistory,
  mdiPackageDown,
  mdiPlaylistCheck,
  mdiFileTree,
  mdiPackageVariant,
} from '@mdi/js';

// Helper
import { removeEmptyValueObject } from '../../utils/helpers';

// Type
import { Props } from './interfaces';
import type { Filter } from '../../utils/listDaynamicFilter';
import parcelsRepository from 'client-sdk/src/Repository/parcels-repository';
import handleError from '../../utils/handleError';
import handleNotfication from '../../utils/handleNotification';
import useQuery from '../../utils/hook/useQuery';

type Inputs = {
  'id.equals': string;
};

// Style
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    paper: {},
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
    pagination: {
      // marginTop: theme.spacing(1),
    },
    textArea: {
      width: '100%',
    },
    searchBox: {
      display: 'flex',
      position: 'relative',
      backgroundColor: theme.palette.common.white,
      borderRadius: '4px',
      padding: '10px 0',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
    },

    input: {
      marginLeft: theme.spacing(1),
      flex: 2,
      '@media (max-width: 768px)': {
        fontSize: '12px',
      },
    },
    iconButton: {
      padding: 10,
      marginRight: '10px',
      position: 'relative',
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '1px',
        height: '36px',
        top: '4px',
        left: '-9px',
        backgroundColor: '#EDF1F4',
      },
    },
    filterBox: {
      position: 'absolute',
      backgroundColor: '#fff',
      width: '100%',
      top: '47px',
      right: '0',
      marginTop: '30px',
      borderRadius: '0  0 4px  4px',
      padding: '16px 16px',
      boxSizing: 'border-box',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
      zIndex: 10,
    },
    secondDetail: {
      backgroundColor: 'white',
      padding: '5px',
      borderRadius: '5px',
      // width: '100%',
    },
    filterOpen: {
      display: 'block',
    },
    filterClose: {
      display: 'none',
    },
    paginationBox: {
      display: 'flex',
    },
    selectSize: {
      width: '35px',
    },
    subtitle: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#8A8A8A',
    },
    magnifyIcon: {
      transform: 'rotate(-90deg)',
    },
    // Cancel Dialog
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'center',
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    editIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      // marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.blue.main,
      },
    },
    addIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      // marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.green.main,
      },
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  })
);

const Parcels: FunctionComponent<Props> = (props: Props) => {
  const { register, handleSubmit } = useForm<Inputs>({
    defaultValues: {
      idType: '',
    },
  });

  const { filtersValues, setFiltersValues } = useQuery({
    page: 0,
    size: 20,
    'status.in': 'ASSIGNED,REGISTERED,UNLOADED',
  } as any);
  const [filters, setFilters] = useState([] as Filter[]);
  const [openConfirmDisable, setOpenConfirmDisable] = useState(
    false as boolean
  );
  const [openDialog, setOpenDialog] = useState(false as boolean);

  const [openAdd, setOpenAdd] = useState(false as boolean);
  const [id, setId] = useState(-1 as number);
  const [openConfirmActive, setOpenConfirmActive] = useState(false as boolean);
  const [loading, setLoading] = useState<boolean>(false);
  const [parcelsList, setParcelsList] = useState([]);
  const [count, setCount] = useState<number>(0);

  const removeFilterHandler = (fieldName: any) => {
    const filtered = { ...filtersValues };
    delete filtered[fieldName];
    setFiltersValues(filtered);
  };
  const pageChangeHandler = (page: any) => {
    setFiltersValues(page);
  };
  const sizeChangeHandler = (size: any) => {
    setFiltersValues(size);
  };
  const filterTranslator = (fieldName: string) => {
    const status = {
      customerName: 'نام سفارش دهنده',
      'customerName.equals': 'نام سفارش دهنده:',
      'customerName.contais': 'نام سفارش دهنده:',
      trackingCode: 'کد پیگیری',
      'trackingCode.equals': 'کد پیگیری',
      'trackingCode.contains': 'کد پیگیری',
      'id.equals': 'شناسه سفارش:',
      'id.contains': 'شناسه سفارش:',
      deliveryDate: 'زمان تحویل',
      'deliveryDate.equals': 'زمان تحویل:',
      'deliveryDate.contains': 'زمان تحویل:',
      status: 'وضعیت سفارش',
      'status.in': 'وضعیت سفارش:',
      'status.notIn': 'وضعیت سفارش:',
    };
    return status[fieldName];
  };
  const setSelectedId = (selected: any) => {
    setId(selected);
  };
  useEffect(() => {
    getParcels();
    setupFilters();
  }, [filtersValues]); // eslint-disable-line react-hooks/exhaustive-deps
  const classes = useStyles();
  const getParcel = async (action: any) => {
    const payload = { ...action };
    try {
      setLoading(true);
      const { response, count } = await parcelsRepository.getParcels({ payload });
      setCount(count.data);
      setParcelsList(response.data);
    } catch (error) {
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };
  const getParcels = () => {
    const { page, size } = filtersValues;
    getParcel({
      ...removeEmptyValueObject(filtersValues),
      page,
      size,
    });
  };

  const handleClose = () => {
    setOpenConfirmDisable(false);
    setOpenDialog(false);
    setOpenAdd(false);
    setOpenConfirmActive(false);
  };

  const handleOpenConfirmUpdateState = (data: { id: number; body: any }) => {
    setId(data.id);
  };

  const setupFilters = () => {
    setFilters([
      {
        type: 'input',
        id: 'sourceWarehouseName.contains',
        label: 'مبدا',
        icon: mdiMapMarkerOutline,
      },
      {
        type: 'input',
        id: 'id.equals',
        label: 'شناسه محموله ',
        icon: mdiKeyOutline,
      },
      {
        type: 'checkbox',
        id: 'status.in',
        label: 'وضعیت محموله',
        data: ['ASSIGNED', 'REGISTERED', 'UNLOADED'],
        translateType: 'ParcelsStatus',
        icon: mdiStateMachine,
      },
    ]);
  };
  let headCells = [];
  headCells = [
    {
      id: 'sourceWarehouseName',
      type: 'input',
      label: 'مبدا',
    },
    {
      id: 'destinationWarehouseName',
      type: 'input',
      label: 'مقصد',
    },
    {
      id: 'id',
      type: 'input',
      label: 'شناسه محموله',
    },
    {
      id: 'inboundType',
      translate: 'parcel',
      translateType: 'parcel',
      type: 'checkbox',
      data: ['NORMAL', 'VIP'],
      label: 'نوع اینباند',
    },
    {
      id: 'deliveryDate',
      type: 'date-picker',
      label: 'تاریخ تحویل',
    },
    {
      id: 'deliveryTime',
      type: 'time-picker',
      label: 'بازه تحویل',
    },
    {
      id: 'items',
      type: 'input',
      label: 'تعدادکارتن',
    },
    {
      id: 'status',
      label: 'وضعیت',
      translate: 'parcel',
      type: 'checkbox',
      data: ['ASSIGNED', 'REGISTERED', 'UNLOADED'],
      translateType: 'parcel',
      filter: 'not'
    },
  ];
  const head = { headCells };
  const updateParcelsStatus = async (action: any) => {
    const payload = { ...action };
    try {
      const callback = await parcelsRepository.updateParcelStatus({ payload });
      handleNotfication('محموله با موفقیت به روز رسانی شد', 'success');
      callback && callback();
    } catch (error) {
      handleError(error.response);
    }
  };

  const handleDisableUser = async () => {
    await updateParcelsStatus({
      id,
      body: 'CANCELLED'.replace(/'/g, '"'),
      pagination: filtersValues,
    });
    setOpenConfirmDisable(false);
  };

  const idSubmitHanler = async (data: any) => {
    const { page, size } = filtersValues;
    const payload = {
      id: data.idType
    };
    try {
      setLoading(true);
      const response = await parcelsRepository.geParcelsWithId(
        { payload }
      );
      switch (response.data.status) {
        case 'ASSIGNED':
          props.history.push(`/parcel/${response.data.id}/unload-inventory-items`)
          break;
        case 'UNLOADED':
          props.history.push(`/parcel/${response.data.id}/inventory-items`)
          break;
        case 'REGISTERED':
          props.history.push(`/parcel/${response.data.id}/locate-inventory-items`)
          break;

      }
    } catch (error) {
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  }
  return (
    <div className={classes.root}>
      <Grid container spacing={6}>
        <Grid container item xs={12} sm={6} alignItems="center">
          <Typography variant="h4" component="h2">
            {'لیست محموله‌ها '}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <RenderFilter
            filters={filters}
            onFilter={(filtervalues: any) => {
              setFiltersValues({
                ...removeEmptyValueObject(filtervalues),
                page: 0,
                size: 20,
                'status.in': 'ASSIGNED,REGISTERED,UNLOADED',
              });
            }}
            mainFiltersCount={1}
            baseFilter={{
              type: 'input',
              id: 'id.equals',
              placeholder: 'شناسه محموله',
            }}
            filterValues={filtersValues}
          />
        </Grid>
        <Grid>
          <form
            style={{ width: '320px', textAlign: 'center', margin: '50px 40px' }}
            onSubmit={handleSubmit(idSubmitHanler)}
          >
            <Grid
              className={classes.secondDetail}
              container
              spacing={2}
              alignItems="center"
            >
              <Grid item xs={9} alignItems="center">
                {/* <span >
                      <Icon path={mdiBarcodeScan} size={1} color="black" />
                    </span> */}
                <span>
                  <TextField
                    fullWidth
                    id="idType"
                    name="idType"
                    size="small"
                    inputRef={register({
                      required: true,
                    })}
                    autoFocus
                    label="شناسه محموله"
                  />
                </span>
              </Grid>
              <Grid item xs={3}>
                <IconButton
                  aria-label="details"
                  type="submit"
                  size="small"
                  title="ادامه پروسه"
                  data-cy="details"
                >
                  <Icon
                    path={mdiPackageVariant}
                    size={1}
                    color={theme.palette.info.main}
                  />
                </IconButton>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          {loading ? (
            <div className={classes.loading}>
              <CircularProgress />
            </div>
          ) : (
            <>
              {parcelsList.length > 0 ? (
                <Helper
                  removeFilterHandler={removeFilterHandler}
                  sizeChanged={sizeChangeHandler}
                  pageChanged={pageChangeHandler}
                  count={count}
                  data={parcelsList}
                  head={head}
                  filterVal={filtersValues}
                  onFilterValue={(filtervalues: any) => {
                    setFiltersValues({
                      ...removeEmptyValueObject(filtervalues),
                      ...filtersValues,
                      page: 0,
                      size: 10,
                    });
                  }}
                  filterLabelTranslator={filterTranslator}
                  changeStatus={handleOpenConfirmUpdateState}
                  setSelectedId={setSelectedId}
                >
                  {(row: any) => {
                    return (
                      <Fragment>
                        {row.status === 'ASSIGNED' ? (
                          <Link to={`/parcel/${row.id}/unload-inventory-items`}>
                            <IconButton size="small" title="تخلیه محموله">
                              <Icon
                                color={theme.palette.orange.main}
                                path={mdiPackageDown}
                                size={1}
                              />
                            </IconButton>
                          </Link>
                        ) : null}
                        {row.status === 'UNLOADED' ? (
                          <Link to={`/parcel/${row.id}/inventory-items`}>
                            <IconButton size="small" title="تطبیق محموله">
                              <Icon
                                color={theme.palette.blue.main}
                                path={mdiPlaylistCheck}
                                size={1}
                              />
                            </IconButton>
                          </Link>
                        ) : null}
                        {row.status === 'REGISTERED' ? (
                          <Link to={`/parcel/${row.id}/locate-inventory-items`}>
                            <IconButton size="small" title="جایگذاری محموله">
                              <Icon color="purple" path={mdiFileTree} size={1} />
                            </IconButton>
                          </Link>
                        ) : null}
                        <Link to={`/parcels/details/${row.id}`}>
                          <IconButton
                            aria-label="details"
                            size="small"
                            title="جزئیات محموله"
                            data-cy="details"
                          >
                            <Icon
                              path={mdiFileDocumentOutline}
                              size={1}
                              color={theme.palette.green.main}
                            />
                          </IconButton>
                        </Link>
                        <Link to={`/parcels/${row.id}/allLogs`}>
                          <IconButton size="small" title="تاریخچه محموله">
                            <Icon path={mdiHistory} size={1} />
                          </IconButton>
                        </Link>
                      </Fragment>
                    );
                  }}
                </Helper>
              ) : (
                <Typography variant="h3" component="div">
                  محموله موجود نیست
                </Typography>
              )}
            </>
          )}
        </Grid>
      </Grid>
      <Dialog
        fullScreen
        open={openDialog}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle>
          <Button aria-label="details" size="medium" onClick={handleClose}>
            <Icon path={mdiArrowRight} size={1} />
            {'بازگشت'}
          </Button>
        </DialogTitle>
      </Dialog>

      <Dialog
        fullWidth
        open={openConfirmDisable || openConfirmActive}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle>
          <div
            style={{
              position: 'absolute',
              left: '17px',
              zIndex: 10,
            }}
          >
            {handleClose ? (
              <IconButton
                aria-label="close"
                // className={classes.closeButton}
                onClick={handleClose}
              >
                <Icon path={mdiClose} size={1} />
              </IconButton>
            ) : null}
          </div>
          <div
            className={
              openConfirmDisable ? classes.cancelIcon : classes.editIcon
            }
          >
            {openConfirmDisable ? (
              <Icon path={mdiClose} size={4} color={theme.palette.error.main} />
            ) : (
              <Icon path={mdiCheck} size={4} color={theme.palette.blue.main} />
            )}
          </div>
          <div className={classes.cancelTitle}>
            {` از 
            
            لغو محموله با شناسه
          "${parcelsList.find(item => item.id === id)?.id}"
          مطمئن هستید؟`}
          </div>
        </DialogTitle>
        <DialogActions>
          <Grid container spacing={1}>
            <Grid item xs={8}>
              <Button
                fullWidth
                autoFocus
                onClick={handleClose}
                variant="contained"
                color="secondary"
                size="large"
              >
                {'خیر'}
              </Button>
            </Grid>

            <Grid item xs={4}>
              <Button
                onClick={handleDisableUser}
                fullWidth
                variant="contained"
                size="large"
              >
                {'بله'}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Parcels;
